import { useEffect, useRef } from 'react';
import { useUser } from '@/hooks/user/useUser.ts';
import { useGetExpiredSession } from './session/useGetExpiredSession';
import { api, useRefreshSessionMutation } from '@/services/ApiService';
import { useAppDispatch } from './useAppDispatch.tsx';

/**
 * When session health returns not healthy (isExpired), try POST /api/session/refresh once
 * before redirecting to logout. If refresh returns 200, session is repacked and we refetch
 * health; if 401, redirect to /api/logout.
 * When skipPolling is true (e.g. session-access cookie mode), no polling or redirect is done.
 */
export const useLogoutAfterMaxPolls = (
    pollEveryNthMinutes: number,
    maxPolls: number,
    options?: { skipPolling?: boolean },
) => {
    const skipPolling = options?.skipPolling ?? false;
    const { user } = useUser();
    const { isExpired, isFetching } = useGetExpiredSession({ skip: skipPolling || !user });
    const [refreshSession] = useRefreshSessionMutation();
    const dispatch = useAppDispatch();
    const refreshAttemptedRef = useRef(false);

    useEffect(() => {
        if (!isExpired) {
            refreshAttemptedRef.current = false;
        }
    }, [isExpired]);

    useEffect(() => {
        if (skipPolling || !user) {
            return;
        }
        {
            let count = 0;

            const logoutAfterMaxChecks = async () => {
                count++;

                if (count > maxPolls) {
                    window.location.href = '/api/logout';
                    return;
                }

                if (isExpired && !isFetching) {
                    if (!refreshAttemptedRef.current) {
                        refreshAttemptedRef.current = true;
                        try {
                            const result = await refreshSession().unwrap();
                            if (result?.ok) {
                                dispatch(api.util.invalidateTags(['User']));
                                return;
                            }
                        }
                        catch {
                            // refresh failed (401 or network error)
                        }
                        window.location.href = '/api/logout';
                        return;
                    }
                    window.location.href = '/api/logout';
                }
            };

            void logoutAfterMaxChecks();

            const interval = setInterval(() => void logoutAfterMaxChecks(), pollEveryNthMinutes * 60 * 1000);

            return () => clearInterval(interval);
        }
    }, [skipPolling, maxPolls, pollEveryNthMinutes, isExpired, isFetching, user, refreshSession, dispatch]);
};
